import { clsx } from 'clsx';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { Verification } from '../api/verificationApi';
import {
  coverageStatusLabel,
  formatFrequency,
  formatMoney,
  formatPercent,
  formatShortDate,
  formatYesNo,
  networkStatusLabel,
} from '../utils/format';
import { NeedsMarker, SourceChip } from './chips';
import { VerifiedValueCard } from './VerifiedValueCard';
import styles from './BenefitBreakdownTab.module.css';

interface BenefitBreakdownTabProps {
  verification: Verification;
}

export function BenefitBreakdownTab({ verification }: BenefitBreakdownTabProps) {
  const { plan, maximums, deductibles, categories, benefitDetails } = verification;

  const maxSub =
    maximums.annualMaximum.value != null
      ? `used ${formatMoney(maximums.annualMaximumUsed.value ?? 0)} · remaining ${formatMoney(
          maximums.annualMaximumRemaining.value ??
            maximums.annualMaximum.value - (maximums.annualMaximumUsed.value ?? 0),
        )}`
      : undefined;

  const dedSub =
    deductibles.individualAnnual.value != null
      ? `met ${formatMoney(deductibles.individualMet.value ?? 0)}`
      : undefined;

  return (
    <div className={styles.root}>
      <section>
        <h3 className={styles.sectionTitle}>Eligibility &amp; plan</h3>
        <div className={styles.kvGrid}>
          <VerifiedValueCard
            label="Coverage status"
            field={plan.coverageStatus}
            format={coverageStatusLabel}
            emptyMarker="not returned"
          />
          <VerifiedValueCard
            label="Network status"
            field={plan.networkStatus}
            format={networkStatusLabel}
            emptyMarker="not returned"
          />
          <VerifiedValueCard
            label="Plan type"
            field={plan.planType}
            format={(v) => v}
            emptyMarker="not returned"
          />
          <VerifiedValueCard
            label="Effective date"
            field={plan.effectiveDate}
            format={formatShortDate}
            emptyMarker="not returned"
          />
        </div>
      </section>

      <section>
        <h3 className={styles.sectionTitle}>Maximums &amp; deductibles</h3>
        <div className={styles.kvGrid}>
          <VerifiedValueCard
            label="Annual maximum"
            field={maximums.annualMaximum}
            format={formatMoney}
            sub={maxSub}
          />
          <VerifiedValueCard
            label="Individual deductible"
            field={deductibles.individualAnnual}
            format={formatMoney}
            sub={dedSub}
          />
          <VerifiedValueCard
            label="Deductible on preventive?"
            field={deductibles.appliesToPreventive}
            format={formatYesNo}
          />
        </div>
      </section>

      <section>
        <h3 className={styles.sectionTitle}>Category coverage</h3>
        <div className={patterns.gridTable}>
          <div className={clsx(patterns.gridTableHeader, styles.catCols)}>
            <span>Category</span>
            <span>Plan pays</span>
            <span>Deductible applies</span>
            <span>Source</span>
          </div>
          {categories.length === 0 && (
            <div className={patterns.gridTableEmpty}>
              No category percentages yet — they arrive with the electronic check.
            </div>
          )}
          {categories.map((cat) => (
            <div key={cat.category} className={clsx(patterns.gridTableRow, styles.catCols)}>
              <div className={patterns.cellSecondary}>{cat.category}</div>
              <div className={patterns.cellPrimary}>
                {cat.coinsurancePercent.value != null ? (
                  formatPercent(cat.coinsurancePercent.value)
                ) : (
                  <NeedsMarker>Needs call</NeedsMarker>
                )}
              </div>
              <div className={patterns.cellSecondary}>
                {formatYesNo(cat.deductibleApplies.value)}
              </div>
              <div>
                <SourceChip source={cat.coinsurancePercent.source} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className={styles.sectionTitle}>Procedure detail (per CDT code)</h3>
        <div className={patterns.gridTable}>
          <div className={clsx(patterns.gridTableHeader, styles.procCols)}>
            <span>Code</span>
            <span>Description</span>
            <span>Plan pays</span>
            <span>Frequency</span>
            <span>Last done</span>
            <span>Source</span>
          </div>
          {benefitDetails.length === 0 && (
            <div className={patterns.gridTableEmpty}>
              No procedure-level detail for this appointment type.
            </div>
          )}
          {benefitDetails.map((d) => {
            const freq = formatFrequency(d.frequency.value);
            const rowSource = d.frequency.source ?? d.coinsurancePercent.source;
            return (
              <div key={d.procedureCode} className={clsx(patterns.gridTableRow, styles.procCols)}>
                <div className={clsx(patterns.cellPrimary, styles.mono)}>{d.procedureCode}</div>
                <div className={patterns.cellSecondary}>
                  {d.description}
                  {d.isExcluded.value && <span className={styles.excluded}> · excluded</span>}
                </div>
                <div className={patterns.cellSecondary}>
                  {d.coinsurancePercent.value != null ? (
                    formatPercent(d.coinsurancePercent.value)
                  ) : (
                    <span className={patterns.cellMuted}>—</span>
                  )}
                </div>
                <div className={patterns.cellSecondary}>
                  {freq ?? <NeedsMarker>Needs call</NeedsMarker>}
                </div>
                <div className={patterns.cellSecondary}>
                  {d.historyLastServiceDate.value ? (
                    formatShortDate(d.historyLastServiceDate.value)
                  ) : (
                    <span className={patterns.cellMuted}>—</span>
                  )}
                </div>
                <div>
                  <SourceChip source={rowSource} />
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
